// Public inquiry form theme — pixel-matched to the Claude Design prototypes.
// Consumes tokens.inquiryForm exactly like buildTheme() consumes tokens.colors.
// Applied via a nested ThemeProvider inside InquiryFormScreen (innermost
// provider wins), so the rest of the app is untouched.
//
// The custom `palette.inquiry` group carries the design's EXACT hex values so
// components never have to approximate with alpha() — components read them via
// useTheme(), keeping the "tokens only in theme files" lint rule intact.
//
// Always light mode: the form is a client-facing branded page, not a tool.

import { createTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
// Registers the MuiPickers* component slots on the theme's `components` map so
// the date-picker input can be themed to match the form's text fields.
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { tokens } from "@/lib/tokens";

const palette = tokens.inquiryForm.colors;
const radius = tokens.inquiryForm.radius;
const fonts = tokens.inquiryForm.typography;
const layout = tokens.inquiryForm.layout;

export interface InquiryPaletteGroup {
  accent: string;
  accentDeep: string;
  accentSoft: string;
  sage: string;
  bg: string;
  ink: string;
  muted: string;
  line: string;
  line2: string;
  error: string;
  errorSoft: string;
  warnBg: string;
  warnBorder: string;
  warnText: string;
  warnIcon: string;
  pausedBg: string;
  pausedText: string;
  noneTileFrom: string;
  noneTileTo: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    inquiry: InquiryPaletteGroup;
  }
  interface PaletteOptions {
    inquiry?: InquiryPaletteGroup;
  }
  // Layout constants the form's components read via useTheme() (tokens may only
  // be imported by this theme file). FormColumn reads inquiryLayout.maxWidth.
  interface Theme {
    inquiryLayout: { maxWidth: number };
  }
  interface ThemeOptions {
    inquiryLayout?: { maxWidth: number };
  }
}

export const INQUIRY_PALETTE: InquiryPaletteGroup = {
  accent: palette.accent,
  accentDeep: palette.accentDeep,
  accentSoft: palette.accentSoft,
  sage: palette.sage,
  bg: palette.bg,
  ink: palette.ink,
  muted: palette.muted,
  line: palette.line,
  line2: "#EEF2EE",
  error: palette.error,
  errorSoft: palette.errorSoft,
  warnBg: palette.warnBg,
  warnBorder: palette.warnBorder,
  warnText: palette.warnText,
  warnIcon: "#B7861F",
  pausedBg: palette.pausedBg,
  pausedText: palette.pausedText,
  // The "Naked / No icing" / "No filling" tile gradient from the designs.
  noneTileFrom: "#EFF2EC",
  noneTileTo: "#DCE5DA",
};

export function buildInquiryTheme(): Theme {
  return createTheme({
    inquiryLayout: { maxWidth: layout.maxWidth },
    palette: {
      mode: "light",
      primary: {
        main: palette.accent,
        dark: palette.accentDeep,
        light: palette.sage,
        contrastText: "#FFFFFF",
      },
      warning: {
        light: palette.warnBg,
        main: palette.pausedText,
        dark: palette.warnText,
      },
      error: { main: palette.error },
      background: {
        default: palette.bg,
        paper: palette.surface,
      },
      text: {
        primary: palette.ink,
        secondary: palette.muted,
      },
      divider: palette.line,
      inquiry: INQUIRY_PALETTE,
    },
    shape: {
      borderRadius: 4,
    },
    typography: {
      fontFamily: fonts.bodyFamily,
      h1: {
        fontFamily: fonts.displayFamily,
        fontWeight: 600,
        fontSize: 40,
        lineHeight: 1.04,
        letterSpacing: "-0.01em",
      },
      h2: {
        // Step headings per design spec: 29px Cormorant.
        fontFamily: fonts.displayFamily,
        fontWeight: 600,
        fontSize: 29,
        lineHeight: 1.1,
      },
      h3: {
        fontFamily: fonts.displayFamily,
        fontWeight: 700,
        fontSize: 23,
        lineHeight: 1.1,
      },
      h4: {
        fontFamily: fonts.displayFamily,
        fontWeight: 600,
        fontSize: 21,
        lineHeight: 1.15,
      },
      subtitle1: { fontWeight: 600, fontSize: 15 },
      subtitle2: { fontWeight: 600, fontSize: 13.5 },
      body1: { fontSize: 15, lineHeight: 1.55 },
      body2: { fontSize: 13.5, lineHeight: 1.5 },
      caption: { fontSize: 12, lineHeight: 1.45 },
      button: {
        fontWeight: 600,
        fontSize: 15,
        textTransform: "none",
      },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.input,
            backgroundColor: palette.surface,
            // 16px+ inside inputs prevents iOS auto-zoom on focus.
            fontSize: 16,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.line,
              borderWidth: 1.5,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.sage,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.accent,
              borderWidth: 1.5,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${palette.accentSoft}`,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.error,
            },
            "&.Mui-error": {
              boxShadow: `0 0 0 3px ${palette.errorSoft}`,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          fullWidth: true,
        },
      },
      // The MUI X date picker renders its own PickersOutlinedInput (not
      // MuiOutlinedInput), so mirror the field styling here to match the other
      // form fields (white fill, rounded, same focus/error rings).
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.input,
            backgroundColor: palette.surface,
            fontSize: 16,
            cursor: "pointer",
            "& .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: palette.line,
              borderWidth: 1.5,
            },
            "&:hover .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: palette.sage,
            },
            "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: palette.accent,
              borderWidth: 1.5,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${palette.accentSoft}`,
            },
            "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: palette.error,
            },
            "&.Mui-error": {
              boxShadow: `0 0 0 3px ${palette.errorSoft}`,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: radius.button,
            minHeight: 48,
          },
          contained: {
            boxShadow: `0 8px 20px -8px ${palette.accent}99`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            backgroundImage: "none",
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            border: `1px solid ${palette.line}`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.pill,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&.Mui-checked": { color: palette.accent },
          },
        },
      },
    },
  });
}
